import { ComponentDataDestService } from "../component-data-dest.service";
import { ComponentDataSourceService } from "../../component-data-source/component-data-source.service";
import { FunctionRegistryService } from '../../function-registry/function-registry.service';
import { catchError, map } from 'rxjs';
import { environment } from '../../../environment/environment';

interface EducationalPathMaintenanceData {
    version_start_date: string;
    version_end_date?: string;
    visibility: string;
    required: boolean;
    modules?: {
        modules: number[];
    } | number[];
    dualPanelSubtypes?: {
        dualPanelSubtypes: number[];
    };
}

interface PathItemRequirement {
    item_type: 'Module' | 'Subtype';
    item_id: number;
    version_start_date: string;
    version_end_date?: string;
    visibility: string;
}

export function put_educational_path_maintenance(
    componentDataDestService: ComponentDataDestService, 
    componentDataSourceService: ComponentDataSourceService, 
    data: Partial<EducationalPathMaintenanceData>
) {
    const LOG_PREFIX = '[Educational Path Maintenance]';
    
    // Extract modules array, handling both formats (array or {modules: array})
    const modulesList = Array.isArray(data.modules) 
        ? data.modules 
        : data.modules?.modules || [];
        
    // Ensure all arrays exist with default empty arrays
    const safeData = {
        ...data,
        subtypes: data.dualPanelSubtypes?.dualPanelSubtypes || [],
        modules: modulesList,
        visibility: data.visibility || 'private'
    };

    // Validate required fields
    if (!safeData.version_start_date) {
        throw new Error('Start date is required');
    }
    const uuid = componentDataSourceService.getCurrentPathUuid();
    if (!uuid) {
        throw new Error('Educational path UUID is required');
    }
    const versionId = componentDataSourceService.getCurrentVersionId();
    if (!versionId) {
        throw new Error('Version ID is required');
    }
    /*if (safeData.required === undefined) {
        throw new Error('Required field must be specified');
    }*/

    // Convert the selections into PathItem format
    const items = [
        ...safeData.subtypes.map((id: number) => ({
            subtype_id: id,
            module_id: null,
            required: safeData.required,
            start_date: safeData.version_start_date!,
            end_date: safeData.version_end_date || null
        })),
        ...safeData.modules.map((id: number) => ({
            module_id: id,
            subtype_id: null,
            required: safeData.required,
            start_date: safeData.version_start_date!,
            end_date: safeData.version_end_date || null
        }))
    ];

    // Validate that at least one requirement is selected
    if (items.length === 0) {
        throw new Error('At least one requirement (subtype or module) must be selected');
    }

    // Validate dates
    const startDate = new Date(safeData.version_start_date);
    if (isNaN(startDate.getTime())) {
        throw new Error('Invalid start date format');
    }
    if (safeData.version_end_date) {
        const endDate = new Date(safeData.version_end_date);
        if (isNaN(endDate.getTime())) {
            throw new Error('Invalid end date format');
        }
        if (endDate < startDate) {
            throw new Error('End date cannot be before start date');
        }
    }

    // Prepare the request data
    const requestData = {
        items
    };

    // Send the requirements to the API
    const request$ = componentDataDestService.http.post(
        `${environment.apiUrl}/paths/versions/${versionId}/maintenance`,
        requestData,
        { withCredentials: true }
    ).pipe(
        catchError(error => {
            console.error(`${LOG_PREFIX} API Error:`, error);
            throw error;
        }),
        map(response => {
            return response;
        })
    );

    // Update the store and refresh modules list on success
    componentDataSourceService.updateStoreData(request$, 'educational_path_maintenance').subscribe({
        next: (response) => {
            try {
                componentDataDestService.injector.get(FunctionRegistryService).executeFunction('refreshEducationalPathModules');
            } catch (e) {
                // Modules component may not be on page
            }
        },
        error: (error) => console.error(`${LOG_PREFIX} Store update error:`, error)
    });
} 